import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { CaseStudy } from '@/mocks/realisations';

type CaseModalProps = {
  study: CaseStudy;
  onClose: () => void;
};

export default function CaseModal({ study, onClose }: CaseModalProps) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const meta = [
    { label: 'Client', value: study.client },
    { label: 'Site', value: study.location },
    { label: 'Année', value: study.year },
    { label: 'Périmètre', value: study.scope },
  ];

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-foreground-950/75 p-4 backdrop-blur-sm md:p-8">
      <div
        className="absolute inset-0"
        role="presentation"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="rise relative z-10 my-4 w-full max-w-3xl overflow-hidden rounded-lg border border-background-200 bg-background-50 md:my-8">
        <div className="relative h-[220px] w-full overflow-hidden bg-foreground-950 md:h-[300px]">
          <img
            src={study.image}
            alt={study.title}
            title={`${study.title} — étude de cas SGSCTIC`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/40 to-foreground-950/20" />
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer l’étude de cas"
            className="absolute right-4 top-4 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-background-50/95 text-foreground-950 transition-colors hover:bg-primary-500 hover:text-background-50"
          >
            <i className="ri-close-line text-xl" />
          </button>
          <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-sm bg-primary-500 px-2.5 py-1 font-label text-[10px] uppercase tracking-[0.16em] text-background-50">
                {study.sector}
              </span>
              <span className="font-label text-[10px] uppercase tracking-[0.16em] text-background-200">
                {study.lead}
              </span>
            </div>
            <h2 className="display-title mt-3 text-3xl text-background-50 md:text-4xl">
              {study.title}
            </h2>
          </div>
        </div>

        <div className="p-5 md:p-8">
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-md bg-background-200 md:grid-cols-4">
            {meta.map((item) => (
              <div key={item.label} className="bg-background-100 px-3 py-3">
                <p className="label-mono text-foreground-500">{item.label}</p>
                <p className="mt-1 text-xs font-semibold leading-snug text-foreground-950">
                  {item.value}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-7 grid gap-6 md:grid-cols-2">
            <div>
              <p className="label-mono text-primary-600">Le défi</p>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">{study.challenge}</p>
            </div>
            <div>
              <p className="label-mono text-primary-600">Notre réponse</p>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">{study.solution}</p>
            </div>
          </div>

          <div className="mt-7 rounded-md border border-background-200 bg-background-100/70 p-5">
            <p className="label-mono text-foreground-500">Résultats</p>
            <ul className="mt-3 space-y-2.5">
              {study.results.map((result) => (
                <li key={result} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-accent-500 text-foreground-950">
                    <i className="ri-check-line text-xs" />
                  </span>
                  <span className="text-sm leading-relaxed text-foreground-800">{result}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {study.expertise.map((item) => (
              <span
                key={item}
                className="rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] text-foreground-600"
              >
                {item}
              </span>
            ))}
          </div>

          <div className="mt-7 flex flex-col gap-3 border-t border-background-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs leading-relaxed text-foreground-500">
              Un projet comparable ? Nous étudions votre besoin.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Link
                to="/contact"
                className="btn-shine flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-background-50 transition-colors hover:bg-primary-600"
              >
                Demander une étude
                <i className="ri-arrow-right-up-line text-sm" />
              </Link>
              <button
                type="button"
                onClick={onClose}
                className="cursor-pointer rounded-md border border-foreground-950/20 px-5 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-foreground-950 transition-colors hover:border-foreground-950"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}